/**
 * Voice Interface Component for LAMED
 * Real-time voice interaction with OpenAI Realtime API
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useModuleRefresh } from '../contexts/ModuleRefreshContext';
import PersonalDataHeader from './PersonalDataHeader';
import { LiveAvatarSession, SessionEvent, SessionState } from '@heygen/liveavatar-web-sdk';
import KnowledgeGraph3D from './KnowledgeGraph3D';

// Connection states
type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'error';

// Recording states
type RecordingState = 'idle' | 'listening' | 'speaking';

// Transcript message interface
interface TranscriptMessage {
  role: 'user' | 'assistant';
  text: string;
  timestamp: Date;
  item_id?: string; // OpenAI conversation item ID (for debugging)
}

// Component props
interface VoiceInterfaceProps {
  minimized?: boolean; // If true, show as floating button (for module routes)
}

const VoiceInterface: React.FC<VoiceInterfaceProps> = ({
  minimized = false,
}) => {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const {
    calendarRefreshRef,
    emailRefreshRef,
    tasksRefreshRef,
    knowledgeRefreshRef
  } = useModuleRefresh();

  // WebSocket connection
  const wsRef = useRef<WebSocket | null>(null);
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');

  // Recording state
  const [recordingState, setRecordingState] = useState<RecordingState>('idle');
  const recordingStateRef = useRef<RecordingState>('idle');

  // Audio elements
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioContextClosingRef = useRef<boolean>(false); // Track if AudioContext is being closed
  const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const processorNodeRef = useRef<ScriptProcessorNode | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);

  // Audio playback queue for sequential playback
  const audioQueueRef = useRef<AudioBuffer[]>([]);
  const isPlayingRef = useRef<boolean>(false);
  const nextPlayTimeRef = useRef<number>(0);
  const currentAudioSourcesRef = useRef<AudioBufferSourceNode[]>([]);
  const isInterruptedRef = useRef<boolean>(false); // Flag to ignore incoming audio after interrupt

  // Note: Voice Activity Detection now handled by OpenAI's server-side VAD
  // Interruptions are triggered by OpenAI's input_audio_buffer.speech_started events

  // Transcript history
  const [transcripts, setTranscripts] = useState<TranscriptMessage[]>([]);
  const transcriptContainerRef = useRef<HTMLDivElement>(null);

  // LAMED COGITO - Symbolic Knowledge State
  const [cogitoKnowledge, setCogitoKnowledge] = useState<any>(null);
  const [cogitoActive, setCogitoActive] = useState<boolean>(false);

  // Cognitive Stream Tab State
  type CognitiveTab = 'default' | 'cogito' | 'transcript';
  const [activeCogitoTab, setActiveCogitoTab] = useState<CognitiveTab>('default');

  // Slide Panel Tab State
  type SlideTab = 'slide' | 'graph';
  const [activeSlideTab, setActiveSlideTab] = useState<SlideTab>('slide');

  // HTML Slide Panel with session history
  const [slideVisible, setSlideVisible] = useState<boolean>(false);
  const [slideTitle, setSlideTitle] = useState<string>('');
  const [slideHtml, setSlideHtml] = useState<string>('');
  const [currentSlideId, setCurrentSlideId] = useState<string | null>(null);
  const slideIframeRef = useRef<HTMLIFrameElement>(null);

  // Slide history for session persistence (max 20 slides)
  interface SlideHistoryItem {
    id: string;
    title: string;
    html: string;
    timestamp: Date;
  }
  const [slideHistory, setSlideHistory] = useState<SlideHistoryItem[]>([]);
  const [showSlideHistory, setShowSlideHistory] = useState<boolean>(false);

  // Session ID
  const sessionIdRef = useRef<string>('');

  // Reconnection state
  const reconnectAttemptsRef = useRef<number>(0);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const maxReconnectAttempts = 5;
  const isManualDisconnectRef = useRef<boolean>(false);

  // Personal data refresh function (for popup)
  const personalDataRefreshRef = useRef<(() => void) | null>(null);

  // Module refresh refs come from context (no need for local refs)

  // Mobile UI state
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  const [mobileView, setMobileView] = useState<'slide' | 'chat'>('slide'); // Toggle between slide and chat on mobile
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);

  // Avatar UI state
  const [transcriptViewMode, setTranscriptViewMode] = useState<'transcript' | 'avatar'>('transcript');
  const transcriptViewModeRef = useRef<'transcript' | 'avatar'>('transcript'); // Ref for closure access
  const avatarVideoRef = useRef<HTMLVideoElement | null>(null);
  const [avatarConnected, setAvatarConnected] = useState<boolean>(false);

  // Fullscreen phone view mode
  const [isPhoneViewFullscreen, setIsPhoneViewFullscreen] = useState<boolean>(false);
  const avatarSessionRef = useRef<LiveAvatarSession | null>(null);
  const [avatarState, setAvatarState] = useState<SessionState | null>(null);
  const avatarInterruptedRef = useRef<boolean>(false); // Track if avatar was interrupted

  // No buffering needed - using native OpenAI audio playback for both modes

  // Avatar mode: Configurable delay (admin only)
  const [avatarAudioDelay, setAvatarAudioDelay] = useState<number>(500); // 0-3000ms in 250ms steps, default 500ms
  const avatarAudioDelayRef = useRef<number>(500); // Ref for closure-safe access in WebSocket handlers
  const isStartOfResponseRef = useRef<boolean>(true);
  const delayedChunksBufferRef = useRef<string[]>([]);
  const isDelayActiveRef = useRef<boolean>(false);

  /**
   * Update recording state (updates both state and ref for VAD access)
   */
  const updateRecordingState = (newState: RecordingState): void => {
    console.log(`State transition: ${recordingStateRef.current} → ${newState}`);
    setRecordingState(newState);
    recordingStateRef.current = newState;
  };

  /**
   * Update transcript view mode (updates both state and ref for WebSocket closure access)
   */
  const updateTranscriptViewMode = (newMode: 'transcript' | 'avatar'): void => {
    console.log(`View mode transition: ${transcriptViewModeRef.current} → ${newMode}`);
    setTranscriptViewMode(newMode);
    transcriptViewModeRef.current = newMode;
  };

  // No buffering logic needed - using native audio playback

  /**
   * Initialize LiveAvatar when user switches to avatar view
   *
   * SECURE: API key stays on backend, only session token returned to frontend
   * Uses LiveAvatar SDK with repeatAudio() to forward OpenAI audio to avatar
   */
  const initializeAvatar = async (): Promise<void> => {
    console.log('🎭 [AVATAR DEBUG] initializeAvatar() called', {
      avatarConnected,
      hasToken: !!token,
      hasExistingSession: !!avatarSessionRef.current
    });

    if (avatarConnected || !token || avatarSessionRef.current) {
      console.log('🎭 [AVATAR DEBUG] Skipping initialization - already initialized or missing requirements');
      return;
    }

    try {
      console.log('🎭 [AVATAR DEBUG] Step 1: Requesting session token from backend...');

      // Step 1: Get session token from backend (SECURE - API key never exposed)
      const response = await fetch('/api/avatar/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          avatar_id: '65cca4cf-b7c8-4619-871f-84e2cf8b21d4' // Katya avatar
        })
      });

      console.log('🎭 [AVATAR DEBUG] Backend response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [AVATAR DEBUG] Backend returned error:', errorText);
        throw new Error(`Failed to create avatar session: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('🎭 [AVATAR DEBUG] Session token received:', {
        session_id: data.session_id,
        hasSessionToken: !!data.session_token,
        tokenLength: data.session_token?.length || 0
      });

      // Step 2: Initialize LiveAvatar SDK with session token for CUSTOM mode
      // CUSTOM mode: No voiceChat needed - we control all audio via repeatAudio()
      // Backend creates session with mode: "CUSTOM" for "Bring Your Own Audio"
      console.log('🎭 [AVATAR DEBUG] Step 2: Creating LiveAvatarSession with voiceChat: false');
      const avatar = new LiveAvatarSession(data.session_token, {
        voiceChat: false  // CUSTOM mode - OpenAI handles all audio, we send via repeatAudio()
      });
      console.log('🎭 [AVATAR DEBUG] LiveAvatarSession created successfully');

      avatarSessionRef.current = avatar;
      console.log('🎭 [AVATAR DEBUG] Avatar session stored in ref');

      // Step 3: Set avatarConnected early so video element renders
      setAvatarConnected(true);
      console.log('🎭 [AVATAR DEBUG] Step 3: avatarConnected set to true');

      // Step 4: Set up event listeners
      // Note: CUSTOM mode does NOT emit events like FULL mode does
      // We handle all state management ourselves
      console.log('🎭 [AVATAR DEBUG] Step 4: Setting up event listeners...');

      avatar.on(SessionEvent.SESSION_STREAM_READY, () => {
        console.log('🎭 [AVATAR DEBUG] ✅ EVENT: SESSION_STREAM_READY fired');
        console.log('🎭 [AVATAR DEBUG] Avatar stream ready - attaching video element');

        setTimeout(() => {
          if (avatarVideoRef.current) {
            console.log('🎭 [AVATAR DEBUG] Video element ref found, calling attach()...');

            // MUTE avatar audio completely (we only want lip-sync animation)
            // We play OpenAI audio separately for better quality
            avatarVideoRef.current.muted = true;
            avatarVideoRef.current.volume = 0;
            console.log('🎭 [AVATAR DEBUG] Video element MUTED for lip-sync only');

            avatar.attach(avatarVideoRef.current);
            console.log('✅ [AVATAR DEBUG] Avatar video stream attached (CUSTOM mode)');

            // Disable all audio tracks from the media stream
            setTimeout(() => {
              if (avatarVideoRef.current && avatarVideoRef.current.srcObject) {
                const stream = avatarVideoRef.current.srcObject as MediaStream;
                const audioTracks = stream.getAudioTracks();
                console.log(`🎭 [AVATAR DEBUG] Found ${audioTracks.length} audio tracks in stream`);

                audioTracks.forEach((track, index) => {
                  track.enabled = false;
                  console.log(`🔇 [AVATAR DEBUG] DISABLED audio track ${index}: ${track.label}`);
                });
              }
            }, 500);
          } else {
            console.error('❌ [AVATAR DEBUG] Video element ref not available');
          }
        }, 100);
      });

      avatar.on(SessionEvent.SESSION_STATE_CHANGED, (state: SessionState) => {
        console.log('🎭 [AVATAR DEBUG] ✅ EVENT: SESSION_STATE_CHANGED:', state);
        console.log('🎭 [AVATAR DEBUG] Updating avatarState from', avatarState, 'to', state);
        setAvatarState(state);
        console.log('🎭 [AVATAR DEBUG] avatarState updated (React will re-render)');
      });

      avatar.on(SessionEvent.SESSION_DISCONNECTED, (reason: any) => {
        console.log('🎭 [AVATAR DEBUG] ✅ EVENT: SESSION_DISCONNECTED:', reason);
        console.warn('⚠️ [AVATAR] Session disconnected - avatar will no longer receive audio!');
        setAvatarConnected(false);
        setAvatarState(null);
        avatarSessionRef.current = null;

        // Auto-reconnect if still in avatar view mode
        if (transcriptViewModeRef.current === 'avatar') {
          console.log('🔄 [AVATAR] Attempting auto-reconnect in 2 seconds...');
          setTimeout(() => {
            if (transcriptViewModeRef.current === 'avatar' && !avatarSessionRef.current) {
              console.log('🔄 [AVATAR] Auto-reconnecting...');
              initializeAvatar().catch(err => {
                console.error('❌ [AVATAR] Auto-reconnect failed:', err);
              });
            }
          }, 2000);
        }
      });

      console.log('🎭 [AVATAR DEBUG] Event listeners registered');

      // Step 5: Start the session
      console.log('🎭 [AVATAR DEBUG] Step 5: Calling avatar.start()...');
      await avatar.start();
      console.log('🎭 [AVATAR DEBUG] ✅ avatar.start() completed successfully');
      console.log('🎭 [AVATAR DEBUG] Avatar session started - CUSTOM mode active (1 credit = 1 minute)');
      console.log('🎭 [AVATAR DEBUG] Avatar is now ready to receive audio via repeatAudio()');

      // Step 6: Monitor max session duration and warn user
      const maxDuration = avatar.maxSessionDuration;
      if (maxDuration) {
        const durationMinutes = Math.floor(maxDuration / 60);
        console.log(`⏱️ [AVATAR] Session max duration: ${maxDuration}s (${durationMinutes} minutes)`);

        // Warn user 1 minute before session expires
        const warningTime = (maxDuration - 60) * 1000;
        if (warningTime > 0) {
          setTimeout(() => {
            console.warn('⚠️ [AVATAR] Session will expire in 1 minute');
            alert('Avatar session will expire in 1 minute. Please save your work.');
          }, warningTime);
        }
      } else {
        console.log('⏱️ [AVATAR] Session has no duration limit');
      }

    } catch (error) {
      console.error('❌ Failed to initialize avatar:', error);
      setAvatarConnected(false);
      avatarSessionRef.current = null;

      alert(`Failed to initialize avatar: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  /**
   * Effect: Initialize avatar when switching to avatar view for the first time
   * Keep avatar session alive even when switching between views
   */
  useEffect(() => {
    if (transcriptViewMode === 'avatar' && !avatarSessionRef.current) {
      console.log('🎭 [AVATAR DEBUG] Avatar view activated - initializing...');
      initializeAvatar();
    }
  }, [transcriptViewMode]);

  /**
   * Effect: Keep-alive mechanism for HeyGen avatar session
   * Sends keep-alive signal every 60 seconds to prevent session timeout
   * Only active when session is in CONNECTED state
   */
  useEffect(() => {
    if (!avatarSessionRef.current || avatarState !== SessionState.CONNECTED) {
      return;
    }

    console.log('📡 [AVATAR KEEP-ALIVE] Starting keep-alive interval (60s)');

    // Send keep-alive every 60 seconds
    const keepAliveInterval = setInterval(async () => {
      if (!avatarSessionRef.current) {
        console.warn('⚠️ [AVATAR KEEP-ALIVE] Session ref lost, stopping interval');
        return;
      }

      try {
        console.log('📡 [AVATAR KEEP-ALIVE] Sending keep-alive signal...');
        await avatarSessionRef.current.keepAlive();
        console.log('✅ [AVATAR KEEP-ALIVE] Keep-alive successful');
      } catch (error) {
        console.error('❌ [AVATAR KEEP-ALIVE] Keep-alive failed:', error);
        // Session may have expired or disconnected
        // The SESSION_DISCONNECTED event handler will clean up
      }
    }, 60000); // 60 seconds

    return () => {
      console.log('📡 [AVATAR KEEP-ALIVE] Stopping keep-alive interval');
      clearInterval(keepAliveInterval);
    };
  }, [avatarState]); // Re-run when avatar state changes

  /**
   * Effect: Cleanup avatar session only when component unmounts
   * This runs once when the component is removed from the DOM
   */
  useEffect(() => {
    return () => {
      if (avatarSessionRef.current) {
        console.log('🎭 [AVATAR DEBUG] Component unmounting - stopping avatar session');
        avatarSessionRef.current.stop().catch((err: Error) => {
          console.error('❌ [AVATAR DEBUG] Error stopping avatar on unmount:', err);
        });
      }
    };
  }, []); // Empty dependency array = runs only on mount/unmount

  /**
   * Handle touch start for swipe gestures
   */
  const handleTouchStart = (e: React.TouchEvent): void => {
    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY
    };
  };

  /**
   * Handle touch end for swipe gestures
   * Swipe left: slide → chat
   * Swipe right: chat → slide
   */
  const handleTouchEnd = (e: React.TouchEvent): void => {
    if (!touchStartRef.current) return;

    const touchEnd = {
      x: e.changedTouches[0].clientX,
      y: e.changedTouches[0].clientY
    };

    const deltaX = touchEnd.x - touchStartRef.current.x;
    const deltaY = Math.abs(touchEnd.y - touchStartRef.current.y);

    // Require horizontal swipe > 100px and vertical movement < 50px
    if (Math.abs(deltaX) > 100 && deltaY < 50) {
      if (deltaX > 0 && mobileView === 'chat') {
        // Swipe right: chat → slide
        setMobileView('slide');
      } else if (deltaX < 0 && mobileView === 'slide') {
        // Swipe left: slide → chat
        setMobileView('chat');
      }
    }

    touchStartRef.current = null;
  };

  /**
   * Toggle mobile view between slide and chat
   */
  const toggleMobileView = (): void => {
    setMobileView(prev => prev === 'slide' ? 'chat' : 'slide');
  };

  /**
   * Redirect to login if no token (separate effect to avoid WebSocket reconnections)
   */
  useEffect(() => {
    if (!token) {
      navigate('/login');
    }
  }, [token, navigate]);

  /**
   * Initialize WebSocket connection - Only once when component mounts
   */
  useEffect(() => {
    if (!token) {
      return; // Don't connect if no token
    }

    console.log('🔌 VoiceInterface: Initializing WebSocket connection (component mounted)');
    connectWebSocket();

    return () => {
      console.log('🔌 VoiceInterface: Cleaning up WebSocket connection (component unmounting)');
      isManualDisconnectRef.current = true;
      disconnectWebSocket(); // This will also call cleanupAudioResources()
    };
  }, [token]); // Only depend on token, not navigate

  // Module refresh refs are accessed directly from context - no setup needed

  /**
   * Auto-scroll transcript to bottom when new messages arrive
   */
  useEffect(() => {
    if (transcriptContainerRef.current) {
      transcriptContainerRef.current.scrollTop = transcriptContainerRef.current.scrollHeight;
    }
  }, [transcripts]);

  /**
   * Auto-restore last slide when returning to /voice route
   * Saves current slide when navigating away, restores when returning
   */
  useEffect(() => {
    // When returning to /voice route (minimized changes from true to false)
    if (!minimized && slideHistory.length > 0) {
      const lastSlide = slideHistory[slideHistory.length - 1];
      console.log(`🔄 Restoring slide on return to /voice: "${lastSlide.title}"`);
      // Always restore the last slide content when returning to /voice
      setSlideTitle(lastSlide.title);
      setSlideHtml(lastSlide.html);
      setSlideVisible(true);
      setCurrentSlideId(lastSlide.id);
    }
  }, [minimized]); // React when minimized prop changes (route changes)

  /**
   * Close slide history dropdown when clicking outside
   */
  useEffect(() => {
    if (!showSlideHistory) return;

    const handleClickOutside = (event: MouseEvent) => {
      // Close dropdown if clicking outside the dropdown menu
      const target = event.target as HTMLElement;
      if (!target.closest('.slide-history-dropdown')) {
        setShowSlideHistory(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showSlideHistory]);

  /**
   * Update iframe content when slide HTML changes or when slide becomes visible
   * Also re-render when returning from minimized mode or switching view modes
   */
  useEffect(() => {
    if (!slideIframeRef.current || !slideHtml || !slideVisible || minimized) {
      return;
    }

    // Small delay to ensure iframe has been moved to correct container
    // This prevents race condition where content is written before DOM manipulation completes
    const timer = setTimeout(() => {
      const iframe = slideIframeRef.current;
      if (!iframe) return;

      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;

      if (iframeDoc) {
        console.log('📄 Writing slide content to iframe:', slideTitle, {
          isPhoneMode: isPhoneViewFullscreen,
          parentContainer: iframe.parentElement?.className || 'unknown'
        });
        // Write complete HTML document to iframe
        iframeDoc.open();
        iframeDoc.write(`
          <!DOCTYPE html>
          <html style="height: 100%; overflow: hidden;">
          <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <style>
              html, body {
                margin: 0;
                padding: 0;
                width: 100%;
                height: 100%;
                overflow: hidden;
                font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                font-size: 16px;
                line-height: 1.6;
                color: #1f2937;
              }
              body {
                padding: 20px;
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
              }
              * {
                box-sizing: border-box;
              }
              /* Ensure images don't overflow */
              img {
                max-width: 100%;
                height: auto;
              }
              /* Ensure tables are responsive */
              table {
                max-width: 100%;
                overflow-x: auto;
                display: block;
              }
              /* Container constraints */
              .slide-content {
                max-width: 100%;
                max-height: 100%;
                overflow: hidden;
              }
            </style>
          </head>
          <body>
            <div class="slide-content">
              ${slideHtml}
            </div>
          </body>
          </html>
        `);
        iframeDoc.close();
      }
    }, 50); // 50ms delay to allow DOM manipulation to complete

    return () => clearTimeout(timer);
  }, [slideHtml, slideVisible, minimized, isPhoneViewFullscreen]); // Re-render iframe when HTML changes, slide visibility changes, returning from minimized, OR switching phone mode

  /**
   * VAD is started/stopped manually during conversation
   * No automatic VAD on connection to avoid premature mic access
   */

  /**
   * Connect to WebSocket
   */
  const connectWebSocket = (): void => {
    if (!token) return;

    setConnectionState('connecting');

    // Determine WebSocket URL based on environment
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    const wsUrl = `${protocol}//${host}/ws/voice`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('WebSocket connected');
        console.log('🧠 [COGITO] Waiting for symbolic knowledge updates from backend...');
        // Send authentication message
        ws.send(JSON.stringify({
          type: 'auth',
          token: token
        }));
      };

      ws.onmessage = (event) => {
        handleWebSocketMessage(event.data);
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setConnectionState('error');
      };

      ws.onclose = (event) => {
        console.log(`🔌 WebSocket disconnected - Code: ${event.code}, Reason: "${event.reason}", Manual: ${isManualDisconnectRef.current}`);
        setConnectionState('disconnected');
        wsRef.current = null;

        // Check for authentication-related close codes
        // 1008 = Policy Violation (often used for auth failures)
        // 1011 = Server error
        // 4001-4099 = Custom auth error codes (if backend uses them)
        const isAuthRelatedClose = event.code === 1008 ||
                                   (event.code >= 4001 && event.code <= 4099) ||
                                   event.reason?.toLowerCase().includes('auth') ||
                                   event.reason?.toLowerCase().includes('token');

        if (isAuthRelatedClose) {
          console.error('🔐 WebSocket closed due to authentication issue - logging out');
          isManualDisconnectRef.current = true;
          logout();
          navigate('/login');
          return;
        }

        // Attempt reconnection if not a manual disconnect
        if (!isManualDisconnectRef.current) {
          console.log(`🔄 Will attempt reconnection (attempt ${reconnectAttemptsRef.current + 1}/${maxReconnectAttempts})`);
          attemptReconnect();
        } else {
          console.log('ℹ️ No reconnection - manual disconnect');
        }
      };
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
      setConnectionState('error');

      // Attempt reconnection on connection failure
      if (!isManualDisconnectRef.current) {
        attemptReconnect();
      }
    }
  };

  /**
   * Attempt to reconnect with exponential backoff
   */
  const attemptReconnect = (): void => {
    if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      setConnectionState('error');
      return;
    }

    // Calculate delay with exponential backoff: 1s, 2s, 4s, 8s, 16s
    const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 16000);
    reconnectAttemptsRef.current += 1;

    console.log(`Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts})`);
    setConnectionState('connecting');

    reconnectTimeoutRef.current = window.setTimeout(() => {
      console.log(`Reconnection attempt ${reconnectAttemptsRef.current}`);
      connectWebSocket();
    }, delay);
  };

  /**
   * Disconnect WebSocket
   */
  /**
   * Clean up all audio resources (prevents Chrome "error 5" crash)
   */
  const cleanupAudioResources = (): void => {
    console.log('🧹 Cleaning up all audio resources...');
    console.log(`📊 Cleanup state: AudioContext=${!!audioContextRef.current}, Playing sources=${currentAudioSourcesRef.current.length}, Queue=${audioQueueRef.current.length}, MediaStream=${!!mediaStreamRef.current}`);

    // Stop all currently playing audio sources
    const sourcesCount = currentAudioSourcesRef.current.length;
    currentAudioSourcesRef.current.forEach((source, index) => {
      try {
        source.stop();
        source.disconnect();
        console.log(`✓ Stopped audio source ${index + 1}/${sourcesCount}`);
      } catch (e) {
        console.warn(`Failed to stop source ${index + 1}:`, e);
      }
    });
    currentAudioSourcesRef.current = [];

    // Clear audio queue
    const queueSize = audioQueueRef.current.length;
    audioQueueRef.current = [];
    isPlayingRef.current = false;
    nextPlayTimeRef.current = 0;
    isInterruptedRef.current = false;
    if (queueSize > 0) {
      console.log(`✓ Cleared audio queue (${queueSize} chunks)`);
    }

    // Disconnect and cleanup recording nodes
    if (processorNodeRef.current) {
      try {
        processorNodeRef.current.disconnect();
        processorNodeRef.current.onaudioprocess = null;
        processorNodeRef.current = null;
        console.log('✓ Processor node cleaned up');
      } catch (e) {
        console.error('Failed to cleanup processor:', e);
      }
    }
    if (gainNodeRef.current) {
      try {
        gainNodeRef.current.disconnect();
        gainNodeRef.current = null;
        console.log('✓ Gain node cleaned up');
      } catch (e) {
        console.error('Failed to cleanup gain:', e);
      }
    }
    if (sourceNodeRef.current) {
      try {
        sourceNodeRef.current.disconnect();
        sourceNodeRef.current = null;
        console.log('✓ Source node cleaned up');
      } catch (e) {
        console.error('Failed to cleanup source:', e);
      }
    }

    // Stop media stream tracks
    if (mediaStreamRef.current) {
      try {
        const tracks = mediaStreamRef.current.getTracks();
        tracks.forEach((track, index) => {
          track.stop();
          console.log(`✓ Stopped media track ${index + 1}/${tracks.length}: ${track.kind}`);
        });
        mediaStreamRef.current = null;
      } catch (e) {
        console.error('Failed to stop media stream:', e);
      }
    }

    // CRITICAL: Close AudioContext to prevent resource leak and Chrome crashes
    if (audioContextRef.current) {
      const contextState = audioContextRef.current.state;
      console.log(`🔊 Closing AudioContext (current state: ${contextState})`);

      if (audioContextClosingRef.current) {
        console.warn('⚠️ AudioContext is already being closed, skipping duplicate close');
      } else {
        audioContextClosingRef.current = true;
        const contextToClose = audioContextRef.current;
        audioContextRef.current = null; // Clear immediately to prevent new usage

        contextToClose.close().then(() => {
          console.log('✅ AudioContext closed successfully');
          audioContextClosingRef.current = false;
        }).catch((err) => {
          console.error('❌ Failed to close AudioContext:', err);
          audioContextClosingRef.current = false;
        });
      }
    } else {
      console.log('ℹ️ No AudioContext to clean up');
    }

    // Reset recording state
    updateRecordingState('idle');
    console.log('✅ Audio cleanup complete');
  };

  const disconnectWebSocket = (): void => {
    // Clear any pending reconnection attempts
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setConnectionState('disconnected');

    // Clean up all audio resources when disconnecting
    cleanupAudioResources();
  };

  /**
   * Add slide to history for session persistence
   */
  const addSlideToHistory = (title: string, html: string): void => {
    const newSlide: SlideHistoryItem = {
      id: Date.now().toString(),
      title: title || 'Untitled Slide',
      html,
      timestamp: new Date()
    };

    setSlideHistory(prev => {
      const updated = [...prev, newSlide];
      // Keep max 20 slides (remove oldest when exceeded)
      return updated.slice(-20);
    });

    setCurrentSlideId(newSlide.id);
    console.log(`📚 Added slide to history: "${title}" (Total: ${slideHistory.length + 1})`);
  };

  /**
   * Load slide from history
   */
  const loadSlideFromHistory = (slide: SlideHistoryItem): void => {
    setSlideTitle(slide.title);
    setSlideHtml(slide.html);
    setSlideVisible(true);
    setCurrentSlideId(slide.id);
    setShowSlideHistory(false);
    console.log(`📖 Loaded slide from history: "${slide.title}"`);
  };

  /**
   * Format timestamp to relative time (e.g., "30 seconds ago")
   */
  const formatRelativeTime = (timestamp: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - timestamp.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffSec < 60) {
      return diffSec === 1 ? '1 second ago' : `${diffSec} seconds ago`;
    } else if (diffMin < 60) {
      return diffMin === 1 ? '1 minute ago' : `${diffMin} minutes ago`;
    } else if (diffHour < 24) {
      return diffHour === 1 ? '1 hour ago' : `${diffHour} hours ago`;
    } else {
      return diffDay === 1 ? '1 day ago' : `${diffDay} days ago`;
    }
  };

  /**
   * Handle WebSocket messages
   */
  const handleWebSocketMessage = (data: string): void => {
    try {
      const message = JSON.parse(data);

      switch (message.type) {
        case 'session.started':
          sessionIdRef.current = message.session_id;
          setConnectionState('connected');
          // Reset reconnection attempts on successful connection
          reconnectAttemptsRef.current = 0;
          console.log('Session started:', message.session_id);
          break;

        case 'audio':
          // CRITICAL: Audio routing based on active view
          // - Avatar tab: Avatar speaks (HeyGen audio with lip-sync)
          // - Transcript tab: Our own audio plays (OpenAI audio)
          // MUST use ref instead of state to avoid closure issues!

          console.log('🎵 [AUDIO ROUTING] Audio message received:', {
            currentViewFromRef: transcriptViewModeRef.current,
            currentViewFromState: transcriptViewMode,
            isAvatarView: transcriptViewModeRef.current === 'avatar',
            isTranscriptView: transcriptViewModeRef.current === 'transcript',
            hasAvatarSession: !!avatarSessionRef.current,
            audioLength: message.audio?.length || 0
          });

          // Avatar mode: Configurable delay before first audio chunk
          const isAvatarMode = transcriptViewModeRef.current === 'avatar';
          const isFirstChunk = isStartOfResponseRef.current;
          const delayMs = avatarAudioDelayRef.current; // Use ref for closure-safe access
          const shouldDelay = isAvatarMode && isFirstChunk && delayMs > 0;
          const isBuffering = isDelayActiveRef.current;

          console.log('🔍 [DELAY CHECK]', {
            isAvatarMode,
            isFirstChunk,
            avatarAudioDelay: delayMs,
            shouldDelay,
            isBuffering
          });

          if (shouldDelay) {
            // FIRST CHUNK: Start delay period and buffer this chunk
            console.log(`⏱️ [AVATAR DELAY] START - ${delayMs}ms delay, buffering all chunks`);
            isStartOfResponseRef.current = false;
            isDelayActiveRef.current = true;
            delayedChunksBufferRef.current = [message.audio];

            // After configured delay, play all buffered chunks
            setTimeout(() => {
              const bufferedChunks = [...delayedChunksBufferRef.current];
              delayedChunksBufferRef.current = [];
              isDelayActiveRef.current = false;

              console.log(`🔊 [AUDIO] DELAY COMPLETE - Playing ${bufferedChunks.length} buffered chunks`);
              bufferedChunks.forEach((chunk) => playAudioChunk(chunk));
            }, delayMs);
          } else if (isBuffering) {
            // SUBSEQUENT CHUNKS DURING DELAY: Add to buffer
            delayedChunksBufferRef.current.push(message.audio);
            console.log(`📦 [AUDIO] BUFFERING - Chunk added (buffer size: ${delayedChunksBufferRef.current.length})`);
          } else {
            // NO DELAY: Play immediately
            playAudioChunk(message.audio);
          }

          // Additionally, send to avatar for lip-sync ONLY (avatar audio muted)
          // No delay needed for lip-sync - avatar can start preparing immediately
          console.log('🎭 [AVATAR CHECK] Before forwarding:', {
            viewMode: transcriptViewModeRef.current,
            hasSession: !!avatarSessionRef.current,
            avatarInterrupted: avatarInterruptedRef.current,
            audioLength: message.audio?.length || 0,
            willForward: transcriptViewModeRef.current === 'avatar' && !!avatarSessionRef.current
          });

          if (transcriptViewModeRef.current === 'avatar' && avatarSessionRef.current) {
            // WARNING: Check if avatar was interrupted and may ignore repeatAudio
            if (avatarInterruptedRef.current) {
              console.warn('⚠️ [AVATAR] Avatar was interrupted - repeatAudio may be ignored!');
              console.warn('⚠️ [AVATAR] Wait for response.done to clear interrupted state');
            }

            try {
              console.log('🎭 [AVATAR LIP-SYNC] Forwarding audio to avatar...');
              // Send raw PCM16 for lip-sync animation (avatar audio output is muted)
              avatarSessionRef.current.repeatAudio(message.audio);
              console.log('✅ [AVATAR LIP-SYNC] Audio forwarded successfully');

              if (avatarInterruptedRef.current) {
                console.warn('⚠️ [AVATAR] Audio sent but avatar is in interrupted state - may not animate');
              }
            } catch (error) {
              console.error('❌ [AVATAR LIP-SYNC] Failed:', error);
              console.error('❌ [AVATAR LIP-SYNC] Session state:', {
                connected: avatarConnected,
                state: avatarState,
                hasSessionRef: !!avatarSessionRef.current,
                interrupted: avatarInterruptedRef.current
              });
            }
          } else {
            console.log('⏭️ [AVATAR SKIP] Not forwarding - view mode or session unavailable');
          }
          break;

        case 'transcript':
          // Add transcript to history with backend timestamp and item_id
          // Backend now sends transcripts in correct order, so no sorting needed
          addTranscript(message.role, message.text, message.timestamp, message.item_id);
          break;

        case 'user.speech_started':
          // OpenAI's server-side VAD detected user speech - interrupt AI
          console.log('🎤 OpenAI detected user speech - interrupting AI...');
          interruptAISpeech();
          break;

        case 'response.done':
          // Audio queue will automatically set to idle when finished playing
          isStartOfResponseRef.current = true;

          // CRITICAL FIX: Clear interrupted flag to ensure next response is not blocked
          // If user interrupted and response.done arrives while interrupted=true,
          // we must clear the flag or ALL future audio will be blocked!
          if (isInterruptedRef.current) {
            console.log('🔓 [RESPONSE] Clearing interrupted flag on response.done');
            isInterruptedRef.current = false;
          }

          // CRITICAL FIX: Clear avatar interrupted state
          // HeyGen SDK documentation shows: interrupt() method exists but NO explicit resume
          // However, calling startListening() may reset the interrupted state
          if (avatarInterruptedRef.current && avatarSessionRef.current) {
            console.log('🔓 [AVATAR] Clearing avatar interrupted state');
            avatarInterruptedRef.current = false;

            // HeyGen LiveAvatarSession API methods (from .d.ts file):
            // - interrupt(): void - stops current speech
            // - repeatAudio(audio: string): void - plays audio for lip-sync
            // - startListening(): void - starts listening mode
            // - stopListening(): void - stops listening mode
            // NO explicit resume/clearInterrupt method exists!

            // Attempt to reset state by calling startListening() then stopListening()
            // This may clear any internal interrupt flags in the SDK
            try {
              avatarSessionRef.current.startListening();
              avatarSessionRef.current.stopListening();
              console.log('✅ [AVATAR] Called startListening/stopListening to reset state');
            } catch (error) {
              console.error('❌ [AVATAR] Error resetting avatar state:', error);
            }
          }

          // Only clear buffer if not in delay period
          if (!isDelayActiveRef.current) {
            delayedChunksBufferRef.current = [];
          }
          console.log('✅ [RESPONSE] Response complete - ready for next response');
          break;

        case 'avatar.ready':
          // HeyGen avatar is ready
          console.log('🎭 Avatar ready:', message.heygen_session_id);
          setAvatarConnected(true);
          // TODO: Initialize HeyGen video stream here when full SDK integration is complete
          break;

        case 'slide':
          // Display HTML slide and add to session history
          console.log('📊 Received slide:', message.title);
          const slideTitle = message.title || 'Slide';
          const slideHtml = message.html_content || '';
          setSlideTitle(slideTitle);
          setSlideHtml(slideHtml);
          setSlideVisible(true);
          addSlideToHistory(slideTitle, slideHtml);
          break;
        case 'personal_data_update':
          // Refresh personal data when backend notifies of changes
          console.log('🔄 Personal data updated:', message.event);

          // Refresh popup (PersonalDataHeader)
          if (personalDataRefreshRef.current) {
            personalDataRefreshRef.current();
          }

          // Refresh specific modules based on event type
          const eventType = message.event || '';

          // Calendar module - event_created, event_updated, event_deleted
          if (eventType.startsWith('event_') && calendarRefreshRef.current) {
            console.log('🗓️ Refreshing Calendar module');
            calendarRefreshRef.current();
          }

          // Email module - email_updated, email_deleted, email_drafted, email_sent
          if (eventType.startsWith('email_') && emailRefreshRef.current) {
            console.log('📧 Refreshing Email module');
            emailRefreshRef.current();
          }

          // Tasks module - task_created, task_updated, task_deleted
          if (eventType.startsWith('task_') && tasksRefreshRef.current) {
            console.log('✅ Refreshing Tasks module');
            tasksRefreshRef.current();
          }

          // Knowledge module - knowledge_created, knowledge_updated, knowledge_deleted
          if (eventType.startsWith('knowledge_') && knowledgeRefreshRef.current) {
            console.log('📚 Refreshing Knowledge module');
            knowledgeRefreshRef.current();
          }
          break;

        case 'error':
          console.error('Server error:', message.message);
          setConnectionState('error');

          // Check if this is an authentication error
          const errorMsg = message.message?.toLowerCase() || '';
          const isAuthError = errorMsg.includes('token') ||
                             errorMsg.includes('auth') ||
                             errorMsg.includes('invalid') ||
                             errorMsg.includes('expired') ||
                             errorMsg.includes('unauthorized');

          if (isAuthError) {
            console.error('🔐 Authentication error detected - logging out and redirecting to login');
            isManualDisconnectRef.current = true;
            disconnectWebSocket();
            logout();
            navigate('/login');
          }
          break;

        case 'cogito':
          // LAMED COGITO: Symbolic knowledge update
          console.log('🧠 [COGITO] Received symbolic knowledge update');
          console.log('🧠 [COGITO] Entities:', message.knowledge?.entities?.length || 0);
          console.log('🧠 [COGITO] Relations:', message.knowledge?.relations?.length || 0);
          console.log('🧠 [COGITO] Facts:', message.knowledge?.facts?.length || 0);
          console.log('🧠 [COGITO] World state:', message.knowledge?.world_state?.context || 'N/A');
          console.log('🧠 [COGITO] Full knowledge object:', message.knowledge);
          setCogitoKnowledge(message.knowledge);
          setCogitoActive(true);
          break;

        default:
          console.log('Unknown message type:', message.type);
      }
    } catch (error) {
      console.error('Failed to parse WebSocket message:', error);
    }
  };

  /**
   * Add transcript message
   * Backend now sends transcripts in correct order using item_id tracking
   */
  const addTranscript = (role: 'user' | 'assistant', text: string, timestamp?: string, item_id?: string): void => {
    setTranscripts(prev => [...prev, {
      role,
      text,
      timestamp: timestamp ? new Date(timestamp) : new Date(),
      item_id
    }]);
  };

  /**
   * Play audio chunk from base64 PCM16 - adds to queue for sequential playback
   */
  const playAudioChunk = async (base64Audio: string): Promise<void> => {
    console.log('🎵 [PLAY AUDIO] playAudioChunk() called', {
      timestamp: Date.now(),
      audioLength: base64Audio?.length || 0,
      isInterrupted: isInterruptedRef.current,
      isPlaying: isPlayingRef.current,
      queueLength: audioQueueRef.current.length
    });

    try {
      // Ignore incoming audio chunks if we're in interrupted state
      if (isInterruptedRef.current) {
        console.log('⚠️ [PLAY AUDIO] Ignoring audio chunk - interrupted state');
        return;
      }

      // Create AudioContext if needed (ensure it's not closed/closing)
      if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
        if (audioContextClosingRef.current) {
          console.warn('⚠️ Cannot create AudioContext - previous one is still closing');
          return; // Skip this audio chunk
        }
        console.log('Creating new AudioContext for playback');
        audioContextRef.current = new AudioContext({ sampleRate: 24000 });
      }

      const audioContext = audioContextRef.current;

      // Ensure AudioContext is running (resume if suspended)
      if (audioContext.state === 'suspended') {
        console.log('Resuming suspended AudioContext');
        await audioContext.resume();
      }

      // Decode base64 to binary
      const binaryString = atob(base64Audio);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Convert PCM16 to Float32
      const pcm16 = new Int16Array(bytes.buffer);
      const float32 = new Float32Array(pcm16.length);
      for (let i = 0; i < pcm16.length; i++) {
        float32[i] = pcm16[i] / 32768.0;
      }

      // Create audio buffer
      const audioBuffer = audioContext.createBuffer(1, float32.length, 24000);
      audioBuffer.getChannelData(0).set(float32);

      // Add to queue
      audioQueueRef.current.push(audioBuffer);
      console.log(`Audio chunk added to queue. Queue length: ${audioQueueRef.current.length}, isPlaying: ${isPlayingRef.current}`);

      // Start playing if not already playing
      if (!isPlayingRef.current) {
        playNextAudioChunk();
      }
    } catch (error) {
      console.error('Failed to play audio:', error);
    }
  };

  /**
   * Play all queued audio chunks with guaranteed gapless playback
   */
  const playNextAudioChunk = (): void => {
    if (!audioContextRef.current) return;

    // Check if we have finished all chunks
    if (audioQueueRef.current.length === 0 && currentAudioSourcesRef.current.length === 0) {
      console.log('Audio queue empty - AI finished speaking, restarting listening...');
      console.log(`Connection state: ${connectionState}, WebSocket state: ${wsRef.current?.readyState}`);
      isPlayingRef.current = false;
      isInterruptedRef.current = false; // Clear interrupted flag when playback completes
      nextPlayTimeRef.current = 0; // Reset timing

      // Automatically start listening again for continuous conversation
      updateRecordingState('idle');
      setTimeout(() => {
        console.log('Attempting to auto-restart recording...');
        startRecording().catch(error => {
          console.error('Failed to auto-restart recording:', error);
          // Stay in idle state if restart fails
        });
      }, 300);  // Increased delay to ensure VAD cleanup completes
      return;
    }

    // Update state when AI starts speaking
    if (!isPlayingRef.current && recordingStateRef.current !== 'speaking') {
      updateRecordingState('speaking');
    }

    isPlayingRef.current = true;
    const audioContext = audioContextRef.current;

    // Schedule ALL queued chunks immediately for gapless playback
    while (audioQueueRef.current.length > 0) {
      const audioBuffer = audioQueueRef.current.shift()!;
      const currentTime = audioContext.currentTime;

      // Calculate start time
      let startTime;
      if (nextPlayTimeRef.current === 0 || nextPlayTimeRef.current < currentTime) {
        // First chunk or catching up - start ASAP
        startTime = currentTime;
      } else {
        // Start exactly when previous chunk ends - ZERO gap!
        startTime = nextPlayTimeRef.current;
      }

      // Create and schedule audio source
      const source = audioContext.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(audioContext.destination);
      source.start(startTime);

      // Track this source for potential interruption
      currentAudioSourcesRef.current.push(source);

      // Update next play time for next chunk (CONSECUTIVE scheduling)
      nextPlayTimeRef.current = startTime + audioBuffer.duration;

      console.log(`Scheduled chunk at ${startTime.toFixed(3)}s, duration ${audioBuffer.duration.toFixed(3)}s, ends ${nextPlayTimeRef.current.toFixed(3)}s`);

      // When last source ends, check if we're done
      source.onended = () => {
        // Use splice for atomic in-place removal to avoid race conditions
        const index = currentAudioSourcesRef.current.indexOf(source);
        if (index > -1) {
          currentAudioSourcesRef.current.splice(index, 1);
        }

        console.log(`Audio chunk ended. Remaining sources: ${currentAudioSourcesRef.current.length}`);

        // If this was the last chunk, trigger cleanup
        if (currentAudioSourcesRef.current.length === 0) {
          playNextAudioChunk();
        }
      };
    }
  };

  /**
   * Interrupt AI speech when user starts talking
   */
  const interruptAISpeech = (): void => {
    // CRITICAL: Use ref, not state - state can be stale due to closures!
    if (recordingStateRef.current !== 'speaking') {
      console.log(`Cannot interrupt - not in speaking state (current: ${recordingStateRef.current})`);
      return;
    }

    // Don't interrupt if we're near the end - let the last chunk finish
    const currentTime = audioContextRef.current?.currentTime || 0;
    const timeRemaining = nextPlayTimeRef.current - currentTime;

    // If less than 1 second of audio remaining and no more chunks queued, let it finish
    if (timeRemaining < 1.0 && audioQueueRef.current.length === 0) {
      console.log(`Not interrupting - only ${timeRemaining.toFixed(2)}s remaining, letting it finish`);
      return;
    }

    console.log(`🛑 User interrupting AI speech! Time remaining: ${timeRemaining.toFixed(2)}s`);

    // Set interrupted flag to ignore incoming audio chunks
    isInterruptedRef.current = true;

    // Stop all currently playing audio
    currentAudioSourcesRef.current.forEach(source => {
      try {
        source.stop();
        source.disconnect();
      } catch (e) {
        // Source might have already stopped
      }
    });
    currentAudioSourcesRef.current = [];

    // Clear audio queue
    audioQueueRef.current = [];
    isPlayingRef.current = false;
    nextPlayTimeRef.current = 0;

    // CRITICAL: Interrupt avatar lip-sync animation with same delay as audio playback
    // This ensures visual sync - avatar stops at same time as audio would have started
    if (avatarSessionRef.current) {
      const delayMs = avatarAudioDelayRef.current; // Use ref for closure-safe access
      if (delayMs > 0) {
        console.log(`🎭 [AVATAR INTERRUPT] Delaying avatar interrupt by ${delayMs}ms for sync`);
        setTimeout(() => {
          try {
            avatarSessionRef.current?.interrupt();
            avatarInterruptedRef.current = true; // Mark avatar as interrupted
            console.log('🎭 [AVATAR INTERRUPT] Avatar interrupted - lip-sync stopped (delayed)');
          } catch (error) {
            console.error('❌ [AVATAR INTERRUPT] Failed to interrupt avatar:', error);
          }
        }, delayMs);
      } else {
        // No delay - interrupt immediately
        try {
          avatarSessionRef.current.interrupt();
          avatarInterruptedRef.current = true; // Mark avatar as interrupted
          console.log('🎭 [AVATAR INTERRUPT] Avatar interrupted - lip-sync stopped (immediate)');
        } catch (error) {
          console.error('❌ [AVATAR INTERRUPT] Failed to interrupt avatar:', error);
        }
      }
    }

    // Send cancel response to server
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'response.cancel'
      }));
    }

    // Start listening to user
    updateRecordingState('idle');
    setTimeout(() => startRecording(), 100);
  };

  /**
   * Start recording
   */
  const startRecording = async (): Promise<void> => {
    // Check WebSocket directly instead of React state (avoids stale closure)
    const wsReady = wsRef.current && wsRef.current.readyState === WebSocket.OPEN;
    console.log(`startRecording() called. WS ready: ${wsReady}, React state: ${connectionState}`);

    if (!wsReady) {
      console.error(`Cannot start recording - WebSocket not ready. ReadyState: ${wsRef.current?.readyState}`);
      throw new Error('Not connected to server');
    }

    // Clear interrupted flag - accepting new audio now
    isInterruptedRef.current = false;

    // Clear audio queue from previous response
    audioQueueRef.current = [];
    isPlayingRef.current = false;
    nextPlayTimeRef.current = 0;

    try {
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 24000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        }
      });

      mediaStreamRef.current = stream;

      // Create AudioContext (ensure it's not closed/closing)
      if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
        if (audioContextClosingRef.current) {
          console.warn('⚠️ Cannot create AudioContext for recording - previous one is still closing');
          throw new Error('AudioContext is still closing - please wait');
        }
        console.log('Creating new AudioContext for recording');
        audioContextRef.current = new AudioContext({ sampleRate: 24000 });
      }

      const audioContext = audioContextRef.current;

      // Ensure AudioContext is running (resume if suspended)
      if (audioContext.state === 'suspended') {
        console.log('Resuming suspended AudioContext for recording');
        await audioContext.resume();
      }

      // Create source from microphone stream
      const source = audioContext.createMediaStreamSource(stream);
      sourceNodeRef.current = source;

      // Clean up old processor if exists (prevent memory leak!)
      if (processorNodeRef.current) {
        console.log('Cleaning up old processor node');
        processorNodeRef.current.disconnect();
        processorNodeRef.current.onaudioprocess = null;
        processorNodeRef.current = null;
      }

      // Clean up old gain node if exists
      if (gainNodeRef.current) {
        gainNodeRef.current.disconnect();
        gainNodeRef.current = null;
      }

      // Create ScriptProcessorNode for real-time audio processing
      // Buffer size 4096 gives us chunks every ~170ms at 24kHz
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      processorNodeRef.current = processor; // Store in ref for cleanup!

      processor.onaudioprocess = (event) => {
        const inputData = event.inputBuffer.getChannelData(0);

        // Convert Float32 to PCM16
        const pcm16 = new Int16Array(inputData.length);
        for (let i = 0; i < inputData.length; i++) {
          const s = Math.max(-1, Math.min(1, inputData[i]));
          pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
        }

        // Convert to base64
        const uint8Array = new Uint8Array(pcm16.buffer);
        const binaryString = String.fromCharCode(...uint8Array);
        const base64Audio = btoa(binaryString);

        // Send to server
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({
            type: 'audio',
            audio: base64Audio
          }));
        }
      };

      // ScriptProcessorNode requires connection to destination for onaudioprocess to fire
      // But we don't want to hear the microphone! Solution: Mute it with a GainNode
      const gainNode = audioContext.createGain();
      gainNode.gain.value = 0; // Mute - no audio output!
      gainNodeRef.current = gainNode; // Store in ref for cleanup!

      // Connect: source -> processor -> gainNode(muted) -> destination
      source.connect(processor);
      processor.connect(gainNode);
      gainNode.connect(audioContext.destination);

      console.log('Recording started - microphone is muted (no feedback)');

      updateRecordingState('listening');
    } catch (error) {
      console.error('Failed to start recording:', error);
      alert('Failed to access microphone. Please check permissions.');
    }
  };

  /**
   * Stop recording (without committing - just close the mic)
   */
  const stopRecording = (sendCommitOnStop: boolean = false): void => {
    console.log(`Stopping recording - cleaning up all audio nodes (commit: ${sendCommitOnStop})`);

    // Disconnect processor node (CRITICAL - prevents memory leak!)
    if (processorNodeRef.current) {
      processorNodeRef.current.disconnect();
      processorNodeRef.current.onaudioprocess = null; // Remove event handler
      processorNodeRef.current = null;
    }

    // Disconnect gain node
    if (gainNodeRef.current) {
      gainNodeRef.current.disconnect();
      gainNodeRef.current = null;
    }

    // Disconnect source node
    if (sourceNodeRef.current) {
      sourceNodeRef.current.disconnect();
      sourceNodeRef.current = null;
    }

    // Stop microphone stream
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }

    // Only send commit if explicitly requested (for VAD-triggered stops)
    if (sendCommitOnStop) {
      sendCommit();
    }

    updateRecordingState('idle');
  };

  /**
   * Send commit message to trigger AI response
   */
  const sendCommit = (): void => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'commit'
      }));
    }
  };

  /**
   * Toggle recording - simple on/off toggle
   * IMPORTANT: Use ref instead of state to avoid stale closure issues
   */
  const toggleRecording = (): void => {
    // Use ref instead of state - state can be stale due to async updates!
    const currentState = recordingStateRef.current;
    console.log(`Button clicked - current state: ${currentState}`);

    if (currentState === 'listening') {
      // Stop microphone when listening (don't commit - just close mic)
      console.log('Stopping microphone (no commit)');
      stopRecording(false);
    } else if (currentState === 'speaking') {
      // Stop AI speaking and go back to idle (don't auto-restart recording)
      console.log('Stopping AI speech');
      stopAISpeech();
    } else if (currentState === 'idle') {
      // Start recording
      console.log('Starting microphone');
      startRecording();
    }
  };

  /**
   * Stop AI speech without restarting recording
   */
  const stopAISpeech = (): void => {
    console.log('🛑 Stopping AI speech');

    // Stop all currently playing audio
    currentAudioSourcesRef.current.forEach(source => {
      try {
        source.stop();
        source.disconnect();
      } catch (e) {
        // Source might have already stopped
      }
    });
    currentAudioSourcesRef.current = [];

    // Clear audio queue
    audioQueueRef.current = [];
    isPlayingRef.current = false;
    nextPlayTimeRef.current = 0;

    // Set interrupted flag to ignore incoming audio chunks
    isInterruptedRef.current = true;

    // Send cancel response to server
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'response.cancel'
      }));
    }

    // Go back to idle (don't auto-restart recording)
    updateRecordingState('idle');
  };

  /**
   * Handle logout
   */
  const handleLogout = (): void => {
    isManualDisconnectRef.current = true;
    disconnectWebSocket();
    logout();
    navigate('/login');
  };

  // If minimized, show only floating button
  if (minimized) {
    return (
      <div
        className="fixed bottom-6 left-6 w-24 h-24 rounded-full shadow-2xl z-50 cursor-pointer transition-all duration-300 ease-in-out transform hover:scale-110"
        style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
        onClick={() => navigate('/voice')}
        title="Return to LAMED Voice Interface"
      >
        <div className="w-full h-full flex items-center justify-center relative">
          {/* Pulse rings when listening (red) */}
          {recordingState === 'listening' && (
            <>
              <div className="voice-pulse absolute inset-0 bg-red-500/30 rounded-full" />
              <div className="voice-pulse-delayed absolute inset-0 bg-red-500/20 rounded-full" />
            </>
          )}

          {/* Pulse rings when speaking (blue/purple) */}
          {recordingState === 'speaking' && (
            <>
              <div className="voice-pulse absolute inset-0 bg-purple-500/30 rounded-full" />
              <div className="voice-pulse-delayed absolute inset-0 bg-blue-500/20 rounded-full" />
            </>
          )}

          <svg
            className="w-12 h-12 text-white z-10"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            {recordingState === 'listening' ? (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            ) : recordingState === 'speaking' ? (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
              />
            ) : (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
              />
            )}
          </svg>

          {/* Connection indicator badge */}
          <div
            className={`absolute top-0 right-0 w-6 h-6 rounded-full border-4 border-white ${
              connectionState === 'connected' ? 'bg-green-500' :
              connectionState === 'connecting' ? 'bg-yellow-500' :
              'bg-red-500'
            }`}
            title={connectionState}
          />
        </div>
      </div>
    );
  }

  // Full interface (when not minimized)
  return (
    <>
      {/* Hidden Video Element - Stays in DOM, attached by HeyGen SDK */}
      <video
        ref={avatarVideoRef}
        autoPlay
        playsInline
        muted={true}
        className="hidden"
        onLoadedData={() => console.log('📱 [VIDEO] Video stream attached and loaded')}
        onError={(e) => console.error('📱 [VIDEO] Video error:', e)}
      />

      {/* Fullscreen Phone View Mode - Three Column Layout */}
      {isPhoneViewFullscreen && transcriptViewMode === 'avatar' && avatarConnected ? (
        <div className="fixed inset-0 z-50 bg-black flex items-center justify-center gap-4 p-4 overflow-hidden">
          {/* Three Column Container with Responsive Sizing */}
          <div
            className="flex items-center justify-center gap-4"
            style={{
              // Calculate max size based on available space
              // Total width needed: slideWidth + phoneWidth + cognitiveWidth + gaps + padding
              // slideWidth = h (square), phoneWidth = h * (9/19.5) ≈ h*0.4615
              // cognitiveWidth = clamp(256px, available, 384px)
              // Total = h + h*0.4615 + cognitiveWidth + gaps(2*16px) + padding(2*16px)
              // = h*1.4615 + cognitiveWidth + 64px
              // For max cognitive (384px): h = (100vw - 448px) / 1.4615
              // But also constrain by height: h <= 95vh
              maxHeight: 'min(95vh, calc((100vw - 64px - clamp(256px, 25vw, 384px)) / 1.4615))',
              height: 'min(95vh, calc((100vw - 64px - clamp(256px, 25vw, 384px)) / 1.4615))',
            }}
          >
            {/* LEFT: Square LAMED Slide Display */}
            <div className="flex flex-col items-center justify-center h-full">
              <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl shadow-2xl border-4 border-gray-700 h-full aspect-square flex flex-col">
                {/* Tab Header */}
                <div className="bg-black/50 backdrop-blur-sm border-b border-gray-700 flex-shrink-0 rounded-t-xl">
                  <div className="flex gap-1 px-2 py-1.5">
                    <button
                      onClick={() => setActiveSlideTab('slide')}
                      className={`px-3 py-1.5 text-[9px] md:text-[10px] font-mono rounded transition-colors ${
                        activeSlideTab === 'slide'
                          ? 'bg-blue-600/50 text-blue-200 border border-blue-400/50'
                          : 'text-gray-400 hover:text-gray-200 hover:bg-gray-700/30'
                      }`}
                    >
                      SLIDE
                    </button>
                    <button
                      onClick={() => setActiveSlideTab('graph')}
                      className={`px-3 py-1.5 text-[9px] md:text-[10px] font-mono rounded transition-colors ${
                        activeSlideTab === 'graph'
                          ? 'bg-blue-600/50 text-blue-200 border border-blue-400/50'
                          : 'text-gray-400 hover:text-gray-200 hover:bg-gray-700/30'
                      }`}
                    >
                      3D GRAPH
                    </button>
                  </div>
                </div>

                {/* Slide Container - Will be populated by DOM manipulation */}
                {activeSlideTab === 'slide' && (
                <div
                  className="flex-1 overflow-hidden bg-white relative"
                  key={`phone-slide-${slideVisible}`}
                  ref={(el) => {
                    if (!el || !isPhoneViewFullscreen) return;

                    // Use requestAnimationFrame to ensure iframe ref is set
                    requestAnimationFrame(() => {
                      const iframe = slideIframeRef.current;
                      if (iframe && iframe.parentElement !== el) {
                        console.log('📱 [PHONE MODE] Moving iframe to phone view slide container');
                        el.appendChild(iframe);
                        iframe.className = 'w-full h-full border-0';
                        iframe.style.width = '100%';
                        iframe.style.height = '100%';
                      }
                    });
                  }}
                >
                  {/* Placeholder if no slide */}
                  {!slideVisible && (
                    <div className="flex items-center justify-center h-full text-gray-500">
                      <div className="text-center">
                        <svg className="w-16 h-16 md:w-24 md:h-24 mx-auto mb-4 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        <p className="text-xs md:text-sm opacity-50">No slide active</p>
                      </div>
                    </div>
                  )}
                </div>
                )}

                {/* 3D Knowledge Graph Tab */}
                {activeSlideTab === 'graph' && (
                  <div className="flex-1 overflow-hidden bg-black relative">
                    <KnowledgeGraph3D knowledge={cogitoKnowledge} />
                  </div>
                )}

                {/* Label */}
                <div className="bg-black/80 backdrop-blur-sm px-3 py-1.5 md:px-4 md:py-2 flex-shrink-0 rounded-b-xl">
                  <p className="text-[10px] md:text-xs text-gray-400 text-center font-mono">
                    {activeSlideTab === 'slide' ? 'LAMED VISUAL OUTPUT' : 'KNOWLEDGE GRAPH'}
                  </p>
                </div>
              </div>
              <div className="mt-2 text-gray-500 text-[10px] md:text-xs font-mono">SLIDE DISPLAY</div>
            </div>

            {/* CENTER: Vertical Phone with Avatar */}
            <div className="flex flex-col items-center justify-center h-full">
              {/* Phone Frame - Vertical Portrait with 9:19.5 aspect ratio */}
              <div
                className="relative bg-black rounded-[2rem] md:rounded-[3rem] shadow-2xl border-4 md:border-8 border-gray-800 h-full"
                style={{ aspectRatio: '9 / 19.5' }}
              >
                {/* Phone Notch */}
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-20 h-4 md:w-32 md:h-6 bg-black rounded-b-2xl z-10"></div>

                {/* Avatar Video Container */}
                <div
                  className="absolute inset-0 rounded-[1.5rem] md:rounded-[2.3rem] overflow-hidden bg-gradient-to-br from-gray-900 to-gray-800"
                  ref={(el) => {
                    if (el && avatarVideoRef.current) {
                      if (avatarVideoRef.current.parentElement !== el) {
                        el.appendChild(avatarVideoRef.current);
                        avatarVideoRef.current.className = 'w-full h-full object-cover';
                      }
                    }
                  }}
                />

                {/* Recording State Indicator - Bottom Center */}
                <div className="absolute bottom-4 md:bottom-8 left-1/2 transform -translate-x-1/2 z-20">
                  {recordingState === 'listening' && (
                    <div className="flex items-center gap-1.5 md:gap-2 px-2.5 md:px-4 py-1.5 md:py-2 rounded-full bg-red-500/90 text-white backdrop-blur-sm">
                      <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-white rounded-full animate-pulse"></div>
                      <span className="text-xs md:text-sm font-medium">REC</span>
                    </div>
                  )}
                  {recordingState === 'speaking' && (
                    <div className="flex items-center gap-1.5 md:gap-2 px-2.5 md:px-4 py-1.5 md:py-2 rounded-full bg-green-500/90 text-black backdrop-blur-sm">
                      <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-black rounded-full animate-pulse"></div>
                      <span className="text-xs md:text-sm font-medium">ACTIVE</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-2 text-gray-500 text-[10px] md:text-xs font-mono">AVATAR INTERFACE</div>
            </div>

            {/* RIGHT: Cognitive Stream - Matrix Style Console */}
            <div className="flex flex-col items-center justify-center h-full">
              <div className="relative bg-black rounded-xl md:rounded-2xl shadow-2xl border-2 md:border-4 border-green-900 h-full flex flex-col" style={{
                width: 'clamp(256px, 25vw, 384px)'
              }}>
                {/* Scanline effect overlay */}
                <div className="absolute inset-0 pointer-events-none rounded-xl md:rounded-2xl" style={{
                  background: 'repeating-linear-gradient(0deg, rgba(0, 255, 0, 0.03) 0px, rgba(0, 255, 0, 0.03) 1px, transparent 1px, transparent 2px)'
                }}></div>

                {/* Header with Tabs */}
                <div className="bg-green-900/30 backdrop-blur-sm border-b border-green-800 flex-shrink-0">
                  <div className="px-3 py-1.5 md:px-4 md:py-2 border-b border-green-800/50">
                    <p className="text-green-400 text-[10px] md:text-xs font-mono flex items-center justify-between">
                      <span className="flex items-center gap-1.5 md:gap-2">
                        <span className="w-1.5 h-1.5 md:w-2 md:h-2 bg-green-400 rounded-full animate-pulse"></span>
                        COGNITIVE STREAM
                      </span>
                      <span className="opacity-50">[ACTIVE]</span>
                    </p>
                  </div>
                  {/* Tab Buttons */}
                  <div className="flex gap-1 px-2 py-1">
                    <button
                      onClick={() => setActiveCogitoTab('default')}
                      className={`px-2 py-1 text-[8px] md:text-[9px] font-mono rounded transition-colors ${
                        activeCogitoTab === 'default'
                          ? 'bg-green-700/50 text-green-300 border border-green-500/50'
                          : 'text-green-500/60 hover:text-green-400 hover:bg-green-900/30'
                      }`}
                    >
                      DEFAULT
                    </button>
                    <button
                      onClick={() => setActiveCogitoTab('cogito')}
                      className={`px-2 py-1 text-[8px] md:text-[9px] font-mono rounded transition-colors ${
                        activeCogitoTab === 'cogito'
                          ? 'bg-green-700/50 text-green-300 border border-green-500/50'
                          : 'text-green-500/60 hover:text-green-400 hover:bg-green-900/30'
                      }`}
                    >
                      COGITO ({cogitoKnowledge?.entities?.length || 0})
                    </button>
                    <button
                      onClick={() => setActiveCogitoTab('transcript')}
                      className={`px-2 py-1 text-[8px] md:text-[9px] font-mono rounded transition-colors ${
                        activeCogitoTab === 'transcript'
                          ? 'bg-green-700/50 text-green-300 border border-green-500/50'
                          : 'text-green-500/60 hover:text-green-400 hover:bg-green-900/30'
                      }`}
                    >
                      TRANSCRIPT ({transcripts.length})
                    </button>
                  </div>
                </div>

                {/* Tab Content */}
                <div className="flex-1 overflow-y-auto p-3 md:p-4" style={{
                  fontFamily: '"Courier New", "Courier", monospace',
                  fontSize: '10px',
                  lineHeight: '1.4'
                }}>
                  {/* DEFAULT TAB: Dual Stream */}
                  {activeCogitoTab === 'default' && (
                    <div className="space-y-3">
                  {/* TRANSCRIPT STREAM */}
                  <div className="border-l-2 border-blue-500/30 pl-2 pb-3 border-b border-blue-500/20">
                    <div className="text-blue-400 text-[9px] mb-2 font-bold">[TRANSCRIPT STREAM]</div>
                    {transcripts.length === 0 ? (
                      <div className="text-blue-500/30 text-[9px]">&gt; NO TRANSCRIPTS YET_</div>
                    ) : (
                      <div className="space-y-1">
                        {transcripts.slice(-8).map((t, idx) => (
                          <div key={idx} className={`text-[9px] ${t.role === 'user' ? 'text-blue-300/90' : 'text-purple-300/90'}`}>
                            <span className="text-blue-500/60">&gt;</span> <span className="font-bold">{t.role === 'user' ? 'USER' : 'LAMED'}:</span> {t.text}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* SYMBOLIC KNOWLEDGE STREAM */}
                  <div>
                    <div className="text-green-400 text-[9px] mb-2 font-bold border-l-2 border-green-500/30 pl-2">[SYMBOLIC KNOWLEDGE - COGITO]</div>
                    {!cogitoActive ? (
                      <div className="text-green-500/30 animate-pulse pl-2">
                        <p className="text-[9px]">&gt; COGITO INITIALIZING...</p>
                        <p className="text-[9px]">&gt; SYMBOLIC PROCESSOR READY</p>
                        <p className="text-[9px]">&gt; AWAITING CONVERSATION DATA_</p>
                      </div>
                    ) : cogitoKnowledge ? (
                      <div className="space-y-2">
                        {/* World State */}
                        {cogitoKnowledge.world_state && (
                          <div className="border-l-2 border-green-500/30 pl-2">
                            <div className="text-green-400 text-[8px] mb-1">[WORLD STATE]</div>
                            <div className="text-green-500/80 text-[9px]">
                              &gt; CONTEXT: {cogitoKnowledge.world_state.context || 'N/A'}
                            </div>
                            {cogitoKnowledge.world_state.active_topics?.length > 0 && (
                              <div className="text-green-400/70 text-[8px] mt-1">
                                &gt; TOPICS: {cogitoKnowledge.world_state.active_topics.join(', ')}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Recent Entities */}
                        {cogitoKnowledge.entities && cogitoKnowledge.entities.length > 0 && (
                          <div className="border-l-2 border-cyan-500/30 pl-2">
                            <div className="text-cyan-400 text-[8px] mb-1">[ENTITIES: {cogitoKnowledge.entities.length}]</div>
                            {cogitoKnowledge.entities.slice(-3).map((entity: any, idx: number) => (
                              <div key={idx} className="text-cyan-300/80 text-[9px] mb-1">
                                &gt; {entity.type}: {entity.name}
                                {entity.attributes && Object.keys(entity.attributes).length > 0 && (
                                  <span className="text-cyan-500/60 ml-1 text-[8px]">
                                    ({Object.entries(entity.attributes).slice(0, 2).map(([k, v]) => `${k}:${v}`).join(', ')})
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Recent Relations */}
                        {cogitoKnowledge.relations && cogitoKnowledge.relations.length > 0 && (
                          <div className="border-l-2 border-yellow-500/30 pl-2">
                            <div className="text-yellow-400 text-[8px] mb-1">[RELATIONS: {cogitoKnowledge.relations.length}]</div>
                            {cogitoKnowledge.relations.slice(-2).map((rel: any, idx: number) => (
                              <div key={idx} className="text-yellow-300/80 text-[9px] mb-1">
                                &gt; {rel.subject} {rel.predicate} {rel.object}
                                <span className="text-yellow-500/60 ml-1 text-[8px]">
                                  [{(rel.confidence * 100).toFixed(0)}%]
                                </span>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Recent Facts */}
                        {cogitoKnowledge.facts && cogitoKnowledge.facts.length > 0 && (
                          <div className="border-l-2 border-green-500/30 pl-2">
                            <div className="text-green-400 text-[8px] mb-1">[FACTS: {cogitoKnowledge.facts.length}]</div>
                            {cogitoKnowledge.facts.slice(-2).map((fact: any, idx: number) => (
                              <div key={idx} className="text-green-300 text-[9px] mb-1">
                                &gt; {fact.statement.length > 80 ? fact.statement.substring(0, 80) + '...' : fact.statement}
                                <span className="text-green-500/60 ml-1 text-[8px]">
                                  [{fact.temporal}]
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-green-500/50 pl-2">
                        <p className="text-[9px]">&gt; PROCESSING...</p>
                      </div>
                    )}
                  </div>
                    </div>
                  )}

                  {/* COGITO TAB: Full Knowledge Base */}
                  {activeCogitoTab === 'cogito' && (
                    <div className="space-y-2">
                      {!cogitoActive ? (
                        <div className="text-green-500/30 animate-pulse">
                          <p className="text-[9px]">&gt; COGITO NOT YET INITIALIZED_</p>
                        </div>
                      ) : cogitoKnowledge ? (
                        <>
                          {/* World State */}
                          {cogitoKnowledge.world_state && (
                            <div className="mb-3 border-l-2 border-green-500/50 pl-2">
                              <div className="text-green-300 text-[10px] font-bold mb-1">[WORLD STATE]</div>
                              <div className="text-green-400 text-[9px] mb-1">
                                &gt; CONTEXT: {cogitoKnowledge.world_state.context}
                              </div>
                              <div className="text-green-400/80 text-[9px] mb-1">
                                &gt; INTENT: {cogitoKnowledge.world_state.user_intent}
                              </div>
                              <div className="text-green-400/80 text-[9px] mb-1">
                                &gt; SENTIMENT: {cogitoKnowledge.world_state.sentiment}
                              </div>
                              {cogitoKnowledge.world_state.active_topics?.length > 0 && (
                                <div className="text-green-400/80 text-[9px]">
                                  &gt; TOPICS: {cogitoKnowledge.world_state.active_topics.join(', ')}
                                </div>
                              )}
                            </div>
                          )}

                          {/* All Entities */}
                          {cogitoKnowledge.entities && cogitoKnowledge.entities.length > 0 && (
                            <div className="mb-3 border-l-2 border-cyan-500/50 pl-2">
                              <div className="text-cyan-300 text-[10px] font-bold mb-1">[ENTITIES: {cogitoKnowledge.entities.length}]</div>
                              {cogitoKnowledge.entities.map((entity: any, idx: number) => (
                                <div key={idx} className="text-cyan-300/90 text-[9px] mb-1">
                                  &gt; {entity.type}: {entity.name}
                                  {entity.attributes && Object.keys(entity.attributes).length > 0 && (
                                    <div className="text-cyan-400/60 ml-3 text-[8px]">
                                      {Object.entries(entity.attributes).map(([k, v]) => `${k}: ${v}`).join(' | ')}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}

                          {/* All Relations */}
                          {cogitoKnowledge.relations && cogitoKnowledge.relations.length > 0 && (
                            <div className="mb-3 border-l-2 border-yellow-500/50 pl-2">
                              <div className="text-yellow-300 text-[10px] font-bold mb-1">[RELATIONS: {cogitoKnowledge.relations.length}]</div>
                              {cogitoKnowledge.relations.map((rel: any, idx: number) => (
                                <div key={idx} className="text-yellow-300/90 text-[9px] mb-1">
                                  &gt; {rel.subject} <span className="text-yellow-400">{rel.predicate}</span> {rel.object}
                                  <span className="text-yellow-400/60 ml-2 text-[8px]">
                                    [confidence: {(rel.confidence * 100).toFixed(0)}%]
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* All Facts */}
                          {cogitoKnowledge.facts && cogitoKnowledge.facts.length > 0 && (
                            <div className="mb-3 border-l-2 border-green-500/50 pl-2">
                              <div className="text-green-300 text-[10px] font-bold mb-1">[FACTS: {cogitoKnowledge.facts.length}]</div>
                              {cogitoKnowledge.facts.map((fact: any, idx: number) => (
                                <div key={idx} className="text-green-300/90 text-[9px] mb-2">
                                  &gt; {fact.statement}
                                  <div className="text-green-400/60 ml-3 text-[8px]">
                                    temporal: {fact.temporal} | confidence: {(fact.confidence * 100).toFixed(0)}%
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="text-green-500/50">
                          <p className="text-[9px]">&gt; PROCESSING...</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* TRANSCRIPT TAB: Full Conversation History */}
                  {activeCogitoTab === 'transcript' && (
                    <div className="space-y-1">
                      {transcripts.length === 0 ? (
                        <div className="text-blue-500/30 text-[9px]">&gt; NO TRANSCRIPTS YET_</div>
                      ) : (
                        transcripts.map((t, idx) => (
                          <div key={idx} className={`text-[9px] ${t.role === 'user' ? 'text-blue-300/90' : 'text-purple-300/90'} mb-2`}>
                            <div className="text-blue-500/60 text-[8px] mb-0.5">
                              [{new Date(t.timestamp).toLocaleTimeString()}]
                            </div>
                            <div>
                              <span className="font-bold">{t.role === 'user' ? 'USER' : 'LAMED'}:</span> {t.text}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="bg-green-900/30 backdrop-blur-sm px-3 py-1.5 md:px-4 md:py-2 rounded-b-xl md:rounded-b-2xl border-t border-green-800 flex-shrink-0">
                  <p className="text-green-400/50 text-[9px] md:text-[10px] font-mono text-center">
                    NEURAL PROCESSING ACTIVE | {transcripts.length} ENTRIES
                  </p>
                </div>
              </div>
              <div className="mt-2 text-gray-500 text-[10px] md:text-xs font-mono">COGNITIVE LOG</div>
            </div>
          </div>

          {/* Exit Button - Top Right Corner of Screen */}
          <button
            onClick={() => setIsPhoneViewFullscreen(false)}
            className="absolute top-4 right-4 md:top-6 md:right-6 p-2 md:p-3 rounded-full bg-red-900/80 hover:bg-red-800 text-white transition-all backdrop-blur-sm border border-red-700"
            title="Exit fullscreen mode"
          >
            <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      ) : null}

      {/* Normal Layout */}
      <div className="min-h-screen w-full grid-pattern bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        {/* Top Bar - Responsive */}
        <div className="fixed top-0 left-0 right-0 p-4 md:p-6 flex justify-between items-center bg-white/70 backdrop-blur-sm z-40">
        {/* Left Side - Logo + Mobile Burger + Status */}
        <div className="flex items-center gap-3">
          {/* Hebrew Lamed Logo */}
          <span className="text-3xl md:text-4xl font-bold bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent select-none" style={{ fontFamily: 'serif' }}>ל</span>

          {/* Burger Menu - Mobile Only */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Toggle menu"
          >
            <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>

          {/* Connection Status */}
          <div className={`status-indicator ${connectionState === 'connected' ? 'connected' : connectionState === 'connecting' ? 'connecting' : 'disconnected'}`} />
          <span className="hidden sm:inline text-sm font-medium text-gray-700">
            {connectionState === 'connected' && 'Connected'}
            {connectionState === 'connecting' && reconnectAttemptsRef.current > 0 &&
              `Reconnecting... (${reconnectAttemptsRef.current}/${maxReconnectAttempts})`}
            {connectionState === 'connecting' && reconnectAttemptsRef.current === 0 && 'Connecting...'}
            {connectionState === 'disconnected' && 'Disconnected'}
            {connectionState === 'error' && 'Connection Error'}
          </span>
        </div>

        {/* Personal Data Modules - Desktop Only */}
        <div className="hidden md:block">
          <PersonalDataHeader
            onRefreshReady={(refreshFn) => {
              personalDataRefreshRef.current = refreshFn;
            }}
          />
        </div>

        {/* Right Side - User Info & Actions */}
        <div className="flex items-center gap-2 md:gap-4">
          <span className="hidden sm:inline text-xs md:text-sm text-gray-600">
            {user?.full_name?.split(' ')[0] || 'User'}
          </span>
          {/* Admin Panel Button - Only visible to admins */}
          {user?.role === 'admin' && (
            <button
              onClick={() => navigate('/admin')}
              className="btn-primary text-xs md:text-sm px-2 md:px-4 py-1 md:py-2 bg-purple-600 hover:bg-purple-700"
              title="Admin Panel"
            >
              Admin
            </button>
          )}
          <button
            onClick={handleLogout}
            className="btn-secondary text-xs md:text-sm px-2 md:px-4 py-1 md:py-2"
          >
            Logout
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-30 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        >
          <div
            className="fixed top-0 left-0 w-80 h-full bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Mobile Menu Header */}
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-800">LAMED</h2>
              <p className="text-sm text-gray-500">Personal Data</p>
            </div>

            {/* Personal Data Modules - Mobile */}
            <div className="p-6">
              <PersonalDataHeader
                onRefreshReady={(refreshFn) => {
                  personalDataRefreshRef.current = refreshFn;
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Main Container - Responsive Layout */}
      <div
        className={`pt-16 md:pt-24 pb-20 md:pb-8 ${
          slideVisible && mobileView === 'slide' ? 'px-0 lg:px-8' : 'px-4 md:px-8'
        } flex flex-col ${
          slideVisible && mobileView === 'slide' ? 'lg:flex-row' : 'md:flex-row'
        } ${
          slideVisible && mobileView === 'slide' ? 'gap-0 lg:gap-8' : 'gap-4 md:gap-8'
        } ${
          slideVisible && mobileView === 'slide' ? 'h-screen' : 'min-h-screen'
        }`}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Slide/Chat Toggle Button - Mobile Only */}
        {slideVisible && (
          <button
            onClick={toggleMobileView}
            className="md:hidden fixed bottom-24 right-4 z-40 bg-purple-600 text-white p-3 rounded-full shadow-lg"
            aria-label="Toggle between slide and chat"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileView === 'slide' ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
              )}
            </svg>
          </button>
        )}

        {/* Left Column - Voice Interface OR Slide Panel */}
        <div className={`
          ${slideVisible && mobileView === 'slide' ? 'w-full h-full lg:flex-1' : 'flex-1'}
          flex ${slideVisible && mobileView === 'slide' ? '' : 'items-center justify-center'} relative
          ${mobileView === 'chat' && slideVisible ? 'hidden lg:flex' : 'flex'}
        `}>
          {/* Voice Interface Panel - Desktop: Glass pane when not started, stays as centered panel */}
          <div
            className={`
              ${slideVisible
                ? 'hidden'
                : 'glass-pane px-6 py-8 md:px-12 md:py-16 max-w-2xl w-full transition-all duration-500 ease-in-out'
              }
              ${!slideVisible && (recordingState !== 'idle' || transcripts.length > 0) ? 'hidden md:block' : ''}
            `}
          >
            {!slideVisible && (
              <>
                {/* Title */}
                <div className="text-center mb-6 md:mb-8">
                  <h1 className="text-3xl md:text-5xl font-bold mb-2">LAMED</h1>
                  <p className="text-sm md:text-base text-gray-600">Learning and Multimodal Exploration Device</p>
                </div>
              </>
            )}

            {/* Voice Visualizer - Desktop Only */}
            <div className={`hidden md:flex flex-col items-center justify-center ${slideVisible ? '' : 'my-12'}`}>
              {/* Microphone/Speaker Button */}
              <div className="relative">
                {/* Pulse rings when listening (red) */}
                {recordingState === 'listening' && (
                  <>
                    <div className="voice-pulse absolute inset-0 bg-red-500/30" />
                    <div className="voice-pulse-delayed absolute inset-0 bg-red-500/20" />
                  </>
                )}

                {/* Pulse rings when speaking (blue/purple) */}
                {recordingState === 'speaking' && (
                  <>
                    <div className="voice-pulse absolute inset-0 bg-purple-500/30" />
                    <div className="voice-pulse-delayed absolute inset-0 bg-blue-500/20" />
                  </>
                )}

                <button
                  onClick={toggleRecording}
                  disabled={connectionState !== 'connected'}
                  className={`
                    relative
                    ${slideVisible ? 'w-20 h-20' : 'voice-visualizer'}
                    ${recordingState === 'listening' ? 'active' : ''}
                    ${recordingState === 'listening' ? 'mic-button-listening' :
                      recordingState === 'speaking' ? 'mic-button-speaking' : 'mic-button-idle'}
                    transition-all duration-300
                    ${connectionState !== 'connected' ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                    ${slideVisible ? 'rounded-full flex items-center justify-center' : ''}
                  `}
                  title={
                    recordingState === 'idle' ? 'Click to start speaking' :
                    recordingState === 'listening' ? 'Click to stop microphone' :
                    'Click to stop AI and microphone'
                  }
                >
                  <svg
                    className={`${slideVisible ? 'w-10 h-10' : 'w-16 h-16'} text-white`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    {recordingState === 'listening' ? (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    ) : recordingState === 'speaking' ? (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
                      />
                    ) : (
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                      />
                    )}
                  </svg>
                </button>
              </div>

              {/* Status Text - Only show when not in floating mode */}
              {!slideVisible && (
                <>
                  <div className="mt-6 text-center">
                    <p className="text-lg font-medium text-gray-700">
                      {recordingState === 'listening' && 'Listening... (click to stop)'}
                      {recordingState === 'speaking' && 'LAMED is speaking... (click to stop)'}
                      {recordingState === 'idle' && connectionState === 'connected' && 'Click to speak'}
                      {connectionState !== 'connected' && 'Connecting to LAMED...'}
                    </p>
                  </div>

                  {/* Instructions */}
                  {transcripts.length === 0 && (
                    <div className="mt-8 text-center text-sm text-gray-500 space-y-2">
                      <p>Click the microphone button to start talking to LAMED</p>
                      <p>Click again to stop (or just pause speaking for LAMED to respond)</p>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Mobile Info Text - Show status when no slide visible */}
            {!slideVisible && (
              <div className="md:hidden text-center mt-8 mb-4">
                <p className="text-sm font-medium text-gray-700">
                  {recordingState === 'listening' && 'Listening...'}
                  {recordingState === 'speaking' && 'LAMED is speaking...'}
                  {recordingState === 'idle' && connectionState === 'connected' && 'Tap microphone to speak'}
                  {connectionState !== 'connected' && 'Connecting...'}
                </p>
                {transcripts.length === 0 && (
                  <p className="text-xs text-gray-500 mt-2">Tap the microphone at the bottom to start</p>
                )}
              </div>
            )}
          </div>

          {/* HTML Slide Panel - Shows when slideVisible is true */}
          {slideVisible && (
            <div className="w-full h-full p-0 md:p-8 flex flex-col overflow-hidden animate-fade-in md:glass-pane md:rounded-3xl">
              {/* Slide Header */}
              <div className="flex justify-between items-center mb-2 md:mb-6 pb-2 md:pb-4 px-2 md:px-0 border-b border-gray-200">
                <h2 className="text-base md:text-3xl font-bold text-gray-800 truncate">{slideTitle}</h2>
                <div className="flex items-center gap-2 relative">
                  {/* History Dropdown Button - Only show if there are slides in history */}
                  {slideHistory.length > 0 && (
                    <div className="relative slide-history-dropdown">
                      <button
                        onClick={() => setShowSlideHistory(!showSlideHistory)}
                        className="text-gray-500 hover:text-gray-700 transition-colors p-2 hover:bg-gray-100 rounded-full"
                        title={`View slide history (${slideHistory.length})`}
                      >
                        <svg className="w-4 h-4 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </button>

                      {/* History Dropdown Menu */}
                      {showSlideHistory && (
                        <div className="absolute top-full right-0 mt-2 w-80 max-h-96 overflow-y-auto bg-white rounded-lg shadow-2xl border border-gray-200 z-50 animate-fade-in">
                          <div className="p-3 border-b border-gray-200 bg-gray-50">
                            <h3 className="text-sm font-semibold text-gray-700">Slide History</h3>
                            <p className="text-xs text-gray-500 mt-1">{slideHistory.length} slide{slideHistory.length !== 1 ? 's' : ''} in this session</p>
                          </div>
                          <div className="divide-y divide-gray-100">
                            {[...slideHistory].reverse().map((slide) => (
                              <button
                                key={slide.id}
                                onClick={() => loadSlideFromHistory(slide)}
                                className={`w-full text-left p-3 hover:bg-blue-50 transition-colors ${
                                  currentSlideId === slide.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                                }`}
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-800 truncate">{slide.title}</p>
                                    <p className="text-xs text-gray-500 mt-1">{formatRelativeTime(slide.timestamp)}</p>
                                  </div>
                                  {currentSlideId === slide.id && (
                                    <span className="text-xs text-blue-600 font-medium whitespace-nowrap">Current</span>
                                  )}
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <button
                    onClick={() => setSlideVisible(false)}
                    className="text-gray-500 hover:text-gray-700 transition-colors p-2 hover:bg-gray-100 rounded-full"
                    title="Close slide"
                  >
                    <svg className="w-4 h-4 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Slide Content - Sandboxed iframe for complete isolation */}
              <div
                className="flex-1 w-full"
                ref={(el) => {
                  // Return iframe to normal position when not in fullscreen phone view
                  if (el && slideIframeRef.current && !isPhoneViewFullscreen) {
                    const iframe = slideIframeRef.current;
                    if (iframe && iframe.parentElement !== el) {
                      console.log('📄 [NORMAL MODE] Moving iframe to normal slide container');
                      el.appendChild(iframe);
                      iframe.className = 'w-full h-full bg-white rounded-none md:rounded-lg shadow-inner border-0';
                    }
                  }
                }}
              >
                <iframe
                  ref={slideIframeRef}
                  className="w-full h-full bg-white rounded-none md:rounded-lg shadow-inner border-0"
                  sandbox="allow-same-origin allow-scripts allow-forms allow-modals"
                  title="HTML Slide Content"
                />
              </div>
            </div>
          )}
        </div>

        {/* Floating Voice Button - Desktop only, appears when slide is visible on /voice route */}
        {!minimized && slideVisible && (
          <div
            className="hidden md:block fixed bottom-6 left-6 w-24 h-24 rounded-full shadow-2xl z-50 cursor-pointer transition-all duration-300 ease-in-out transform hover:scale-110"
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            }}
            onClick={toggleRecording}
            title={recordingState === 'idle' ? 'Click to speak' : recordingState === 'listening' ? 'Listening...' : 'AI is speaking'}
          >
            <div className="w-full h-full flex items-center justify-center relative">
              {/* Pulse rings when listening (red) */}
              {recordingState === 'listening' && (
                <>
                  <div className="voice-pulse absolute inset-0 bg-red-500/30 rounded-full" />
                  <div className="voice-pulse-delayed absolute inset-0 bg-red-500/20 rounded-full" />
                </>
              )}

              {/* Pulse rings when speaking (blue/purple) */}
              {recordingState === 'speaking' && (
                <>
                  <div className="voice-pulse absolute inset-0 bg-purple-500/30 rounded-full" />
                  <div className="voice-pulse-delayed absolute inset-0 bg-blue-500/20 rounded-full" />
                </>
              )}

              <svg
                className="w-12 h-12 text-white z-10"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                {recordingState === 'listening' ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                ) : recordingState === 'speaking' ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                  />
                )}
              </svg>

              {/* Connection indicator badge */}
              <div
                className={`absolute top-0 right-0 w-6 h-6 rounded-full border-4 border-white ${
                  connectionState === 'connected' ? 'bg-green-500' :
                  connectionState === 'connecting' ? 'bg-yellow-500' :
                  'bg-red-500'
                }`}
                title={connectionState}
              />
            </div>
          </div>
        )}

        {/* Right Column - Conversation Transcript */}
        {!(mobileView === 'slide' && slideVisible && window.innerWidth < 1024) && (
        <div className="flex-1 md:w-96 lg:w-96 flex flex-col">
          <div className="glass-pane flex flex-col h-full max-h-[calc(100vh-10rem)] md:max-h-[calc(100vh-8rem)]">
            {/* Transcript Header with Avatar Toggle */}
            <div className="px-4 md:px-6 py-3 md:py-4 border-b border-gray-200 flex justify-between items-center">
              <div>
                <h2 className="text-lg md:text-xl font-bold text-gray-800">Conversation</h2>
                <p className="text-xs md:text-sm text-gray-500">Live transcript</p>
              </div>

              {/* Avatar/Transcript Toggle Switch */}
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 bg-gray-100 rounded-full p-1">
                  <button
                    onClick={() => updateTranscriptViewMode('avatar')}
                    className={`px-3 py-1.5 rounded-full text-xs md:text-sm font-medium transition-all ${
                      transcriptViewMode === 'avatar'
                        ? 'bg-purple-600 text-white shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                    title="Show avatar video"
                  >
                    Avatar
                  </button>
                  <button
                    onClick={() => updateTranscriptViewMode('transcript')}
                    className={`px-3 py-1.5 rounded-full text-xs md:text-sm font-medium transition-all ${
                      transcriptViewMode === 'transcript'
                        ? 'bg-purple-600 text-white shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                    title="Show text transcript"
                  >
                    Transcript
                  </button>
                </div>

                {/* Phone View Fullscreen Toggle - HIDDEN (kept for future use) */}
                {transcriptViewMode === 'avatar' && avatarConnected && (
                  <button
                    onClick={() => {
                      console.log('📱 [PHONE VIEW] Toggling phone view. Current state:', {
                        isPhoneViewFullscreen,
                        avatarConnected,
                        hasVideoRef: !!avatarVideoRef.current,
                        videoSrc: avatarVideoRef.current?.src,
                        videoReadyState: avatarVideoRef.current?.readyState
                      });
                      setIsPhoneViewFullscreen(!isPhoneViewFullscreen);
                    }}
                    className="hidden p-2 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 transition-all"
                    title={isPhoneViewFullscreen ? "Exit phone view" : "Enter phone view fullscreen"}
                  >
                    {isPhoneViewFullscreen ? (
                      // Exit fullscreen icon
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    ) : (
                      // Fullscreen icon (phone)
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    )}
                  </button>
                )}
              </div>

              {/* Admin Only: Avatar Audio Delay Control */}
              {user?.role === 'admin' && (
                <div className="bg-gray-100 rounded-lg p-3 mt-2">
                  <label className="flex items-center justify-between text-xs font-medium text-gray-700 mb-2">
                    <span>Avatar Audio Delay: {avatarAudioDelay}ms</span>
                    <span className="text-gray-500">(Admin Only)</span>
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="3000"
                    step="250"
                    value={avatarAudioDelay}
                    onChange={(e) => {
                      const newValue = Number(e.target.value);
                      setAvatarAudioDelay(newValue);
                      avatarAudioDelayRef.current = newValue; // CRITICAL: Update ref for closure-safe access
                    }}
                    className="w-full h-2 bg-gray-300 rounded-lg appearance-none cursor-pointer accent-purple-600"
                    title="Delay before playing AI audio in avatar mode (0-3000ms)"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>0ms</span>
                    <span>750ms</span>
                    <span>1500ms</span>
                    <span>2250ms</span>
                    <span>3000ms</span>
                  </div>
                </div>
              )}
            </div>

            {/* Content Area - Avatar or Transcript */}
            {transcriptViewMode === 'avatar' ? (
              /* Avatar Video Display */
              <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-gray-900 to-gray-800 overflow-hidden">
                {avatarConnected ? (
                  !isPhoneViewFullscreen ? (
                    /* Normal Avatar View - Container for video element */
                    <div
                      className="w-full h-full flex items-center justify-center"
                      ref={(el) => {
                        if (el && avatarVideoRef.current && !isPhoneViewFullscreen) {
                          // Move the video element into this container
                          if (avatarVideoRef.current.parentElement !== el) {
                            el.appendChild(avatarVideoRef.current);
                            avatarVideoRef.current.className = 'w-full h-full object-contain';
                            avatarVideoRef.current.style.maxHeight = '100%';
                          }
                        }
                      }}
                    />
                  ) : (
                    /* Placeholder when in phone view - video is rendered in fullscreen overlay */
                    <div className="text-center text-white/70 p-6">
                      <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      <p className="text-sm mb-2">Phone View Active</p>
                      <p className="text-xs opacity-50">Avatar is displayed in fullscreen phone view</p>
                    </div>
                  )
                ) : (
                  <div className="text-center text-white/70 p-6">
                    <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <p className="text-sm mb-2">
                      {avatarState === SessionState.CONNECTING ? 'Connecting to avatar...' : 'Avatar initializing...'}
                    </p>
                    <p className="text-xs opacity-50">
                      {avatarState ? `Status: ${avatarState}` : 'Please wait while we connect to the avatar service'}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              /* Transcript Messages */
              <div ref={transcriptContainerRef} className="flex-1 overflow-y-auto p-3 md:p-6 space-y-3 md:space-y-4">
                {transcripts.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-center text-gray-400">
                    <div>
                      <svg className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-3 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                      <p className="text-xs md:text-sm">Your conversation will appear here</p>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Transcripts are already in correct order from backend */}
                    {transcripts.map((transcript, index) => (
                      <div
                        key={index}
                        className={`flex ${transcript.role === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[85%] rounded-2xl px-3 py-2 md:px-4 md:py-3 ${
                            transcript.role === 'user'
                              ? 'bg-blue-500 text-white rounded-br-sm'
                              : 'bg-gray-100 text-gray-800 rounded-bl-sm'
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-semibold opacity-70">
                              {transcript.role === 'user' ? 'You' : 'LAMED'}
                            </span>
                            <span className="text-xs opacity-50">
                              {transcript.timestamp.toLocaleTimeString()}
                            </span>
                          </div>
                          <p className="text-xs md:text-sm leading-relaxed">{transcript.text}</p>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        )}
      </div>

      {/* Bottom Microphone Bar - Mobile Only */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-sm border-t border-gray-200 p-4 z-50">
        <div className="flex items-center justify-center gap-4">
          {/* Recording State Indicator */}
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${
              recordingState === 'listening' ? 'bg-red-500 animate-pulse' :
              recordingState === 'speaking' ? 'bg-purple-500 animate-pulse' :
              'bg-gray-300'
            }`} />
            <span className="text-xs text-gray-600">
              {recordingState === 'listening' && 'Listening'}
              {recordingState === 'speaking' && 'Speaking'}
              {recordingState === 'idle' && 'Idle'}
            </span>
          </div>

          {/* Microphone Button */}
          <button
            onClick={toggleRecording}
            disabled={connectionState !== 'connected'}
            className={`
              relative w-16 h-16 rounded-full flex items-center justify-center
              ${recordingState === 'listening' ? 'bg-red-500' :
                recordingState === 'speaking' ? 'bg-purple-500' : 'bg-blue-500'}
              ${connectionState !== 'connected' ? 'opacity-50' : 'opacity-100'}
              transition-all duration-300 shadow-lg
            `}
            aria-label={
              recordingState === 'idle' ? 'Start speaking' :
              recordingState === 'listening' ? 'Stop listening' :
              'Stop AI response'
            }
          >
            {/* Pulse rings for active states */}
            {recordingState === 'listening' && (
              <div className="absolute inset-0 rounded-full bg-red-500/30 animate-ping" />
            )}
            {recordingState === 'speaking' && (
              <div className="absolute inset-0 rounded-full bg-purple-500/30 animate-ping" />
            )}

            <svg className="w-8 h-8 text-white z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {recordingState === 'listening' ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              ) : recordingState === 'speaking' ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
              )}
            </svg>
          </button>

          {/* Connection Status */}
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${
              connectionState === 'connected' ? 'bg-green-500' :
              connectionState === 'connecting' ? 'bg-yellow-500 animate-pulse' :
              'bg-red-500'
            }`} />
            <span className="text-xs text-gray-600">
              {connectionState === 'connected' && 'Connected'}
              {connectionState === 'connecting' && 'Connecting'}
              {connectionState === 'disconnected' && 'Offline'}
            </span>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default VoiceInterface;
